/* ============================================
   RAPTOR MAP — Mapbox GL JS Controller
   ============================================
   Symbol-based pins — unaffected by Mapbox
   Standard 3D lighting model.
   Teardrop shape with circular logo inside.
   Falls back to event-type color icon.
   ============================================ */

var RaptorMap = (function() {
  'use strict';

  var map = null;
  var config = {};
  var hoverPopup = null;
  var geojsonData = null;
  var vendorsData = null;

  /* ══════════════════════════════════════════
     EVENT TYPE CONFIG — Single source of truth
     Aligned to GeoJSON event_type values + mockup palette.
     ══════════════════════════════════════════ */
  var PIN_COLORS = {
    /* RUNS — green */
    'run & event - multi day': '#3ddc6e',
    'run - multi day':         '#3ddc6e',
    'run - one day':           '#3ddc6e',
    'run & event - one day':   '#3ddc6e',
    /* EVENTS — Ford blue, others pink */
    'ford event':              '#4a9eff',
    'race':                    '#ff3d8a',
    'expo':                    '#ff3d8a',
    'event - multi day':       '#ff3d8a',
    'car show':                '#ff3d8a',
    /* SOCIAL — orange */
    'caravan':                 '#ff851b',
    'charity':                 '#ff851b',
    'meetup':                  '#ff851b',
    /* CLUB (separate toggle, not in dropdown) — yellow accent */
    'club':                    '#a855f7'
  };
  var DEFAULT_PIN_COLOR = '#aaaaaa';

  var loadedIcons = {};

  function normalizeType(str) {
    return str ? str.trim().toLowerCase() : '';
  }

  function getPinColor(eventType) {
    return PIN_COLORS[normalizeType(eventType)] || DEFAULT_PIN_COLOR;
  }

  /* Touch device = coarse primary pointer. Drives cooperative
     gestures (two-finger pan) and bottom-sheet flyTo padding. */
  function isTouchDevice() {
    return window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
  }

  function isMobileViewport() {
    return window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
  }

  /* flyTo padding that respects panel placement: left rail on
     desktop, bottom sheet on mobile. */
  function panelPadding(panel) {
    if (isMobileViewport()) {
      return { top: 20, bottom: panel.offsetHeight + 20, left: 20, right: 20 };
    }
    return { left: panel.offsetWidth + 40, top: 40, bottom: 40, right: 40 };
  }

  /* ══════════════════════════════════════════
     INIT
     ══════════════════════════════════════════ */
  function init(opts) {
    config = opts;
    loadedIcons = {};

    if (map) {
      map.remove();
      map = null;
    }

    mapboxgl.accessToken = config.accessToken;

    map = new mapboxgl.Map({
      container: config.containerId,
      style: config.styleUrl,
      center: config.defaultCenter,
      zoom: config.defaultZoom,
      attributionControl: false,
      projection: 'mercator',
      /* Touch devices: two fingers pan the map, one finger scrolls
         the page. Prevents the inline map from trapping page scroll. */
      cooperativeGestures: isTouchDevice()
    });

    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-left');
    map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right');

    map.on('load', function() {
      loadGeoJSON();
      bindFullscreen();
      bindPanelClose();
      bindReset();
      listenForCardEvents();
    });
  }

  /* ══════════════════════════════════════════
     DATA LOADING
     ══════════════════════════════════════════ */
  function loadGeoJSON() {
    var url = config.geojsonUrl;
    if (!url) { console.error('RaptorMap: No GeoJSON URL configured'); return; }

    fetch(url)
      .then(function(res) { return res.json(); })
      .then(function(data) {
        geojsonData = jitterDuplicates(data);
        preloadIcons(geojsonData, function() {
          addGeoJSONSource(geojsonData);
          addClusterLayers();
          addPinLayer();
          bindHoverTooltips();
          bindPinClick();
          bindClusterClick();
          lastFilteredCount = geojsonData.features.length;
          updateCount();
          applyFilters();
          loadVendors();
        });
      })
      .catch(function(err) { console.error('RaptorMap: Failed to load GeoJSON', err); });
  }

  /* ══════════════════════════════════════════
     LOAD VENDORS — parallel data source
     ══════════════════════════════════════════ */
  function loadVendors() {
    var url = config.vendorsGeojsonUrl;
    if (!url) return;

    fetch(url)
      .then(function(res) { return res.json(); })
      .then(function(data) {
        vendorsData = jitterDuplicates(data);
        ensureVendorIcon(function() {
          addVendorSource(vendorsData);
          addVendorLayer();
          bindVendorInteractions();
          applyVendorFilters();
        });
      })
      .catch(function(err) { console.error('RaptorMap: Failed to load vendors', err); });
  }

  /* Vendor pin: same teardrop as event pins, red. Reuses renderTearDropCanvas
     and registerIcon so the visuals are consistent. */
  function ensureVendorIcon(callback) {
    if (loadedIcons['pin-type-vendor']) return callback();
    renderTearDropCanvas('#c22a1b', 'vendor', function(canvas) {
      registerIcon('pin-type-vendor', canvas);
      callback();
    });
  }

  function addVendorSource(data) {
    map.addSource('raptor-vendors', {
      type: 'geojson',
      data: data
    });
  }

  function addVendorLayer() {
    map.addLayer({
      id: 'raptor-vendor-pins',
      type: 'symbol',
      source: 'raptor-vendors',
      layout: {
        'icon-image': 'pin-type-vendor',
        'icon-size': ['interpolate', ['linear'], ['zoom'], 3, 0.38, 8, 0.52, 12, 0.7],
        'icon-anchor': 'bottom',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'visibility': 'none'
      },
      paint: { 'icon-opacity': 1 }
    });
  }

  function bindVendorInteractions() {
    map.on('mouseenter', 'raptor-vendor-pins', function() { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', 'raptor-vendor-pins', function() { map.getCanvas().style.cursor = ''; });

    map.on('mousemove', 'raptor-vendor-pins', function(e) {
      if (!e.features || !e.features.length) return;
      var p = e.features[0].properties;
      var name = p.name || 'Vendor';
      var cat  = p.category || '';
      var html = '<strong class="rn-tooltip__name">' + escapeHTML(name) + '</strong>';
      if (cat) html += '<span class="rn-tooltip__type">' + escapeHTML(cat) + '</span>';
      if (hoverPopup) hoverPopup.setLngLat(e.lngLat).setHTML(html).addTo(map);
    });
    map.on('mouseleave', 'raptor-vendor-pins', function() { if (hoverPopup) hoverPopup.remove(); });

    map.on('click', 'raptor-vendor-pins', function(e) {
      if (!e.features || !e.features.length) return;
      var f = e.features[0];
      var p = f.properties;
      if (hoverPopup) hoverPopup.remove();

      var panel = document.getElementById(config.panelId);
      var content = document.getElementById(config.panelContentId);
      if (!panel || !content) return;

      content.innerHTML = buildVendorPanel(p);
      panel.classList.add('is-open');
      map.flyTo({
        center: f.geometry.coordinates,
        zoom: Math.max(map.getZoom(), 8),
        duration: 800,
        padding: panelPadding(panel),
        essential: true
      });
    });
  }

  function buildVendorPanel(p) {
    var name = (p.name || 'Vendor').trim();
    var category = (p.category || '').trim();
    var html = '';

    /* Header — same layout as event panel header */
    html += '<div class="rn-panel__header">';
    html += '<div class="rn-panel__logo-wrap">';
    if (p.logo_url) {
      html += '<img class="rn-panel__logo-img" src="' + escapeHTML(p.logo_url) + '" alt="' + escapeHTML(name) + '">';
    } else {
      html += '<div class="rn-panel__logo-monogram" style="background:#ffffff20;color:#ffffff;">' + escapeHTML(name.charAt(0).toUpperCase()) + '</div>';
    }
    html += '</div></div>';

    /* Body */
    html += '<div class="rn-panel__body">';
    if (category) html += '<span class="rn-panel__badge rn-panel__badge--vendor">' + escapeHTML(category) + '</span>';
    html += '<h3 class="rn-panel__name">' + escapeHTML(name) + '</h3>';
    if (p.description) html += '<p class="rn-panel__description">' + escapeHTML(p.description) + '</p>';
    html += '</div>';

    /* Meta block — location + website */
    var locationParts = [p.city, p.state, p.country].filter(function(v) { return v && String(v).trim(); });
    if (locationParts.length || p.website) {
      html += '<div class="rn-panel__meta rn-panel__meta--divider">';
      if (locationParts.length) html += metaRow(iconPin(), escapeHTML(locationParts.join(', ')));
      if (p.website)            html += metaRow(iconGlobe(), '<a class="rn-panel__link" href="' + escapeHTML(p.website) + '" target="_blank" rel="noopener">Visit Website</a>');
      html += '</div>';
    }

    /* Member-only perk row */
    if (config.isMember && p.premium_perk && String(p.premium_perk).trim()) {
      html += '<div class="rn-panel__meta rn-panel__meta--divider">';
      html += metaRow(iconDiscount(), escapeHTML(p.premium_perk));
      html += '</div>';
    } else if (!config.isMember && p.premium_perk && String(p.premium_perk).trim()) {
      html += '<div class="rn-panel__meta rn-panel__meta--divider rn-panel__gated-wrap">';
      html += '<div class="rn-panel__gated-blur">' + metaRow(iconDiscount(), 'Member perk available') + '</div>';
      html += '<div class="rn-panel__gated-lock">' + iconLock() + '<span class="rn-panel__gated-label">Premium Exclusive</span><a href="/pages/membership" class="rn-panel__gated-btn">Join the Nation</a></div>';
      html += '</div>';
    }

    return html;
  }

  function applyVendorFilters() {
    if (!map || !map.getLayer('raptor-vendor-pins')) return;

    map.setLayoutProperty('raptor-vendor-pins', 'visibility',
      mapFilterState.vendorsOn ? 'visible' : 'none');

    if (!vendorsData || !mapFilterState.vendorsOn) return;

    var selectedTypes = (mapFilterState.vendorTypes || []).map(function(s) {
      return String(s).toLowerCase().trim();
    });

    var filtered = {
      type: 'FeatureCollection',
      features: vendorsData.features.filter(function(f) {
        var p = f.properties || {};

        /* Category filter */
        if (selectedTypes.length > 0) {
          var cat = String(p.category || '').toLowerCase().trim();
          if (selectedTypes.indexOf(cat) === -1) return false;
        }

        /* Region filter */
        if (mapFilterState.region !== 'all') {
          if (getRegion(p) !== mapFilterState.region) return false;
        }

        return true;
      })
    };

    var src = map.getSource('raptor-vendors');
    if (src) src.setData(filtered);
  }

  /* ══════════════════════════════════════════
     JITTER DUPLICATE COORDINATES
     ══════════════════════════════════════════ */
  function jitterDuplicates(data) {
    var RADIUS = 0.005;
    var groups = {};

    data.features.forEach(function(f, i) {
      var coords = f.geometry.coordinates;
      var key = coords[0].toFixed(4) + ',' + coords[1].toFixed(4);
      if (!groups[key]) groups[key] = [];
      groups[key].push(i);
    });

    var features = data.features.map(function(f) {
      return JSON.parse(JSON.stringify(f));
    });

    Object.keys(groups).forEach(function(key) {
      var indices = groups[key];
      if (indices.length < 2) return;
      var count = indices.length;
      indices.forEach(function(idx, i) {
        var angle = (2 * Math.PI / count) * i;
        var lng = features[idx].geometry.coordinates[0] + RADIUS * Math.cos(angle);
        var lat = features[idx].geometry.coordinates[1] + RADIUS * Math.sin(angle);
        features[idx].geometry.coordinates = [lng, lat];
      });
    });

    return { type: 'FeatureCollection', features: features };
  }

  /* ══════════════════════════════════════════
     TYPE-BASED ICON SVG PATHS
     ══════════════════════════════════════════ */
  /* TYPE_ICONS — Canvas drawing functions, keyed to GeoJSON event_type values.
     Currently NOT rendered into pins (pins render as dark circles); retained
     for future icon design. Keys must match the locked taxonomy. */
  var TYPE_ICONS = {
    /* RUNS — mountain peak iconography */
    'run - one day': function(ctx) {
      /* Single mountain peak */
      ctx.beginPath();
      ctx.moveTo(2, 20); ctx.lineTo(12, 4); ctx.lineTo(22, 20); ctx.closePath();
      ctx.fill();
    },
    'run - multi day': function(ctx) {
      /* Multi-peak range */
      ctx.beginPath();
      ctx.moveTo(2, 20); ctx.lineTo(8, 6); ctx.lineTo(11, 11);
      ctx.lineTo(14, 6); ctx.lineTo(22, 20); ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = ctx.fillStyle;
      ctx.beginPath();
      ctx.moveTo(6.5, 14); ctx.lineTo(8, 12); ctx.lineTo(9.5, 14);
      ctx.stroke();
    },
    'run & event - one day': function(ctx) {
      /* Mountain peak with flag — run + event */
      ctx.beginPath();
      ctx.moveTo(2, 20); ctx.lineTo(12, 4); ctx.lineTo(22, 20); ctx.closePath();
      ctx.fill();
      ctx.fillRect(11, 3, 1, 6); ctx.fillRect(12, 3, 4, 3);
    },
    'run & event - multi day': function(ctx) {
      /* Multi-peaks with flag */
      ctx.beginPath();
      ctx.moveTo(2, 20); ctx.lineTo(8, 6); ctx.lineTo(11, 11);
      ctx.lineTo(14, 6); ctx.lineTo(22, 20); ctx.closePath();
      ctx.fill();
      ctx.fillRect(7.5, 5, 1, 5); ctx.fillRect(8.5, 5, 3, 2);
    },

    /* EVENTS */
    'ford event': function(ctx) {
      /* Star */
      ctx.beginPath();
      var cx = 12, cy = 12, or = 8, ir = 3.5;
      for (var i = 0; i < 10; i++) {
        var r = i % 2 === 0 ? or : ir;
        var a = (Math.PI / 2 * -1) + (Math.PI / 5) * i;
        if (i === 0) ctx.moveTo(cx + r * Math.cos(a), cy + r * Math.sin(a));
        else ctx.lineTo(cx + r * Math.cos(a), cy + r * Math.sin(a));
      }
      ctx.closePath(); ctx.fill();
    },
    'race': function(ctx) {
      /* Checkered flag */
      ctx.fillRect(5, 3, 4, 4); ctx.fillRect(13, 3, 4, 4);
      ctx.fillRect(9, 7, 4, 4); ctx.fillRect(5, 11, 4, 4);
      ctx.fillRect(13, 11, 4, 4); ctx.fillRect(9, 3, 4, 4);
      ctx.globalAlpha = 0.35;
      ctx.fillRect(9, 3, 4, 4); ctx.fillRect(5, 7, 4, 4);
      ctx.fillRect(13, 7, 4, 4); ctx.fillRect(9, 11, 4, 4);
      ctx.globalAlpha = 1;
      ctx.strokeRect(5, 3, 12, 12);
      ctx.lineWidth = 2.5;
      ctx.beginPath(); ctx.moveTo(5, 15); ctx.lineTo(5, 22); ctx.stroke();
    },
    'expo': function(ctx) {
      /* Booth grid */
      ctx.fillRect(3, 3, 8, 8);
      ctx.fillRect(13, 3, 8, 8);
      ctx.fillRect(3, 13, 8, 8);
      ctx.globalAlpha = 0.4;
      ctx.fillRect(13, 13, 8, 8);
      ctx.globalAlpha = 1;
    },
    'event - multi day': function(ctx) {
      /* Calendar with multiple pages */
      ctx.lineWidth = 1.5;
      ctx.strokeRect(4, 5, 16, 15);
      ctx.fillRect(4, 5, 16, 5);
      ctx.beginPath(); ctx.moveTo(8, 3); ctx.lineTo(8, 7); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(16, 3); ctx.lineTo(16, 7); ctx.stroke();
      ctx.fillRect(8, 13, 3, 3); ctx.fillRect(13, 13, 3, 3);
    },
    'car show': function(ctx) {
      /* Car silhouette */
      ctx.fillRect(3, 11, 18, 5);
      ctx.beginPath();
      ctx.moveTo(5, 11); ctx.quadraticCurveTo(7, 6, 12, 6);
      ctx.quadraticCurveTo(17, 6, 19, 11); ctx.closePath();
      ctx.fill();
      ctx.beginPath(); ctx.arc(7, 17, 2, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(17, 17, 2, 0, Math.PI * 2); ctx.fill();
    },

    /* SOCIAL */
    'caravan': function(ctx) {
      /* Two vehicles in formation */
      ctx.fillRect(3, 10, 8, 5); ctx.fillRect(13, 10, 8, 5);
      ctx.beginPath(); ctx.arc(5.5, 16, 1.5, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(9, 16, 1.5, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(15.5, 16, 1.5, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(19, 16, 1.5, 0, Math.PI * 2); ctx.fill();
      ctx.fillRect(4, 7, 6, 3); ctx.fillRect(14, 7, 6, 3);
    },
    'charity': function(ctx) {
      /* Heart */
      ctx.beginPath();
      ctx.moveTo(12, 21);
      ctx.bezierCurveTo(12, 21, 2, 14, 2, 8);
      ctx.bezierCurveTo(2, 4, 5, 2, 7.5, 2);
      ctx.bezierCurveTo(9.5, 2, 11, 3, 12, 5);
      ctx.bezierCurveTo(13, 3, 14.5, 2, 16.5, 2);
      ctx.bezierCurveTo(19, 2, 22, 4, 22, 8);
      ctx.bezierCurveTo(22, 14, 12, 21, 12, 21);
      ctx.closePath(); ctx.fill();
    },
    'meetup': function(ctx) {
      /* People group */
      ctx.beginPath(); ctx.arc(9, 7, 3, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(16.5, 7, 2.5, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath();
      ctx.moveTo(3, 19); ctx.quadraticCurveTo(3, 13, 9, 13);
      ctx.quadraticCurveTo(15, 13, 15, 19); ctx.fill();
      ctx.beginPath();
      ctx.moveTo(13, 18); ctx.quadraticCurveTo(13, 13, 16.5, 13);
      ctx.quadraticCurveTo(21, 13, 21, 18); ctx.fill();
    },

    /* CLUB — separate toggle, organization rather than event */
    'club': function(ctx) {
      /* People group with star */
      ctx.beginPath(); ctx.arc(9, 7, 3, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(16.5, 7, 2.5, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath();
      ctx.moveTo(3, 19); ctx.quadraticCurveTo(3, 13, 9, 13);
      ctx.quadraticCurveTo(15, 13, 15, 19); ctx.fill();
      ctx.beginPath();
      ctx.moveTo(13, 18); ctx.quadraticCurveTo(13, 13, 16.5, 13);
      ctx.quadraticCurveTo(21, 13, 21, 18); ctx.fill();
    },

    'default': function(ctx) {
      /* Simple pin / location dot */
      ctx.beginPath(); ctx.arc(12, 12, 5, 0, Math.PI * 2); ctx.fill();
      ctx.globalAlpha = 0.3;
      ctx.beginPath(); ctx.arc(12, 12, 8, 0, Math.PI * 2); ctx.fill();
      ctx.globalAlpha = 1;
    }
  };

  /* ══════════════════════════════════════════
     ICON PRELOADING
     ══════════════════════════════════════════ */
  function preloadIcons(data, callback) {
    var typesNeeded = {};

    data.features.forEach(function(f) {
      var type = normalizeType(f.properties.event_type || '');
      typesNeeded[type || 'default'] = getPinColor(type);
    });

    var keys = Object.keys(typesNeeded);
    var pending = keys.length;
    if (pending === 0) { callback(); return; }

    keys.forEach(function(type) {
      var color = typesNeeded[type];
      var iconName = 'pin-type-' + (type || 'default');
      if (loadedIcons[iconName]) { pending--; if (pending <= 0) callback(); return; }
      renderTearDropCanvas(color, type, function(canvas) {
        registerIcon(iconName, canvas);
        pending--;
        if (pending <= 0) callback();
      });
    });
  }

  /* ══════════════════════════════════════════
     TEARDROP CANVAS RENDERER
     ══════════════════════════════════════════ */
  function renderTearDropCanvas(color, eventType, callback) {
    var W = 80;
    var H = Math.round(W * (440.85 / 256.36));
    var canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    var ctx = canvas.getContext('2d');

    var scale = W / 256.36;

    ctx.save();
    ctx.scale(scale, scale);

    /* ── Drop shadow ── */
    ctx.shadowColor = 'rgba(0,0,0,0.55)';
    ctx.shadowBlur = 12 / scale;
    ctx.shadowOffsetY = 5 / scale;

    /* ── Pin body — exact Raptor Nation SVG path ── */
    var pinPath = new Path2D(
      'M254.81,160.32c-3.84-23.96-14.38-45.15-29.38-62.04h.04S161.2,25.64,161.2,25.64l-12.42,' +
      '28.87L98.62,0l-30.12,66.31c-.32.16-.64.32-.96.49l-16.4-23.53L12.97,122.06h.04C1.84,144.56-2.7,' +
      '170.72,1.6,197.53c3.21,20.03,10.99,38.16,22.06,53.52h-.04s136.87,187.88,136.87,187.88c1.26,1.74,' +
      '3.3,2.23,5.07,1.74-.03-.04-.07-.09-.1-.14-12.75-19.03-13.58-32.12-11.08-41.12,3.53-12.75,13.63-17.24,' +
      '12.04-26.36-2.89-16.56-41.78-16.65-43.48-29.15-2.08-15.23,53.72-31.23,51.81-39.59-2.33-10.21-87.57,' +
      '5.29-90.56-5.34-.83-2.94,4.85-7.26,14.18-12.4-41.69-11.33-75.1-46.09-82.36-91.45-4.32-26.98,1.31-53.29,' +
      '14.17-75.19.93-1.59,1.9-3.16,2.91-4.7.6-.92,1.24-1.81,1.86-2.71.46-.67.91-1.34,1.39-2,.68-.93,1.38-1.83,' +
      '2.09-2.74.46-.6.92-1.2,1.4-1.79.74-.91,1.49-1.8,2.26-2.69.48-.56.96-1.11,1.45-1.66.79-.89,1.6-1.76,' +
      '2.42-2.62.49-.52.99-1.03,1.49-1.54.85-.86,1.7-1.7,2.58-2.53.5-.48,1.02-.95,1.53-1.42.9-.83,1.81-1.65,' +
      '2.73-2.44.5-.43,1.02-.85,1.54-1.27.97-.8,1.94-1.61,2.94-2.38.41-.32.83-.61,1.25-.92,2.17-1.63,4.39-3.18,' +
      '6.68-4.65.42-.27.83-.56,1.25-.82.89-.56,1.82-1.08,2.73-1.62.8-.47,1.59-.94,2.39-1.39.86-.48,1.74-.92,' +
      '2.61-1.38.9-.47,1.8-.94,2.72-1.38.84-.41,1.7-.8,2.55-1.19.98-.45,1.98-.89,2.98-1.31.83-.35,1.66-.69,' +
      '2.5-1.01,1.07-.42,2.15-.82,3.24-1.21.8-.29,1.6-.57,2.42-.84,1.18-.39,2.37-.76,3.57-1.11.75-.22,1.5-.45,' +
      '2.25-.66,1.37-.37,2.75-.71,4.14-1.03.61-.14,1.22-.31,1.83-.44,2.02-.44,4.07-.83,6.14-1.16,2.51-.4,5.01-.71,' +
      '7.5-.94.41-.04.81-.04,1.22-.08,2.11-.17,4.21-.31,6.31-.37.33,0,.66,0,.99,0,2.19-.04,4.37-.03,6.54.06.18,0,' +
      '.35.02.52.03,12.26.57,24.14,3.12,35.27,7.39,0,0,.02-.01.02-.02,3.15,1.21,6.25,2.52,9.27,4l-.06.05c31.99,' +
      '15.67,56.17,46.09,62.22,83.86,9.88,61.7-32.28,119.93-93.98,129.81-14.5,2.32-28.8,1.74-42.34-1.27-5.2,3.21,' +
      '-8.08,5.93-7.53,7.94,2.75,10.14,89.93-5.86,92.47,4.82,2.14,8.96-57.84,26.98-55.37,42.96,1.93,12.51,40.47,' +
      '11.9,43.41,28.72,1.58,9.01-8.53,13.46-12.09,26.06-2.51,8.89-1.71,21.82,11,40.6.03.04.06.09.1.14,1.42-.4,' +
      '2.68-1.41,3.21-3.03l71.78-218.69h-.04c5.76-17.84,7.53-37.26,4.36-57.04Z'
    );

    ctx.fillStyle = color;
    ctx.fill(pinPath);

    ctx.shadowColor = 'transparent';

    /* ── Circle area ── */
    var cx = 127.95;
    var cy = 177.32;
    var r  = 101.71;

    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fill();
    ctx.restore();

    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(255,255,255,0.55)';
    ctx.lineWidth = 5;
    ctx.stroke();

    ctx.restore();

    callback(canvas);
  }

  function registerIcon(iconName, canvas) {
    if (map.hasImage(iconName)) { loadedIcons[iconName] = true; return; }
    var ctx = canvas.getContext('2d');
    var imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    map.addImage(iconName, { width: canvas.width, height: canvas.height, data: imageData.data });
    loadedIcons[iconName] = true;
  }

  /* ══════════════════════════════════════════
     SOURCE + LAYERS
     ══════════════════════════════════════════ */
  function addGeoJSONSource(data) {
    map.addSource('raptor-runs', {
      type: 'geojson',
      data: data,
      cluster: true,
      clusterMaxZoom: 8,
      clusterRadius: 30
    });
  }

  function addClusterLayers() {
    map.addLayer({
      id: 'raptor-clusters',
      type: 'circle',
      source: 'raptor-runs',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': '#e1ef2e',
        'circle-opacity': 1,
        'circle-emissive-strength': 1,
        'circle-radius': ['step', ['get', 'point_count'], 20, 5, 26, 15, 32],
        'circle-stroke-width': 2.5,
        'circle-stroke-color': '#000000',
        'circle-stroke-opacity': 1
      }
    });

    map.addLayer({
      id: 'raptor-cluster-count',
      type: 'symbol',
      source: 'raptor-runs',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'],
        'text-size': 13,
        'text-allow-overlap': true
      },
      paint: { 'text-color': '#000000', 'text-emissive-strength': 1 }
    });
  }

  function addPinLayer() {
    map.addLayer({
      id: 'raptor-pins',
      type: 'symbol',
      source: 'raptor-runs',
      filter: ['!', ['has', 'point_count']],
      layout: {
        'icon-image': [
          'concat',
          'pin-type-',
          ['downcase', ['coalesce', ['get', 'event_type'], 'default']]
        ],
        'icon-size': ['interpolate', ['linear'], ['zoom'], 3, 0.38, 8, 0.52, 12, 0.7],
        'icon-anchor': 'bottom',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true
      },
      paint: { 'icon-opacity': 1 }
    });

    map.on('mouseenter', 'raptor-pins', function() { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', 'raptor-pins', function() { map.getCanvas().style.cursor = ''; });
    map.on('mouseenter', 'raptor-clusters', function() { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', 'raptor-clusters', function() { map.getCanvas().style.cursor = ''; });
  }

  /* ══════════════════════════════════════════
     INTERACTIONS
     ══════════════════════════════════════════ */
  function bindClusterClick() {
    map.on('click', 'raptor-clusters', function(e) {
      var features = map.queryRenderedFeatures(e.point, { layers: ['raptor-clusters'] });
      if (!features.length) return;
      var clusterId = features[0].properties.cluster_id;
      map.getSource('raptor-runs').getClusterExpansionZoom(clusterId, function(err, zoom) {
        if (err) return;
        map.easeTo({ center: features[0].geometry.coordinates, zoom: zoom + 1, duration: 500 });
      });
    });
  }

  function bindFullscreen() {
    var btn = document.getElementById(config.fullscreenId);
    var viewport = document.getElementById(config.viewportId);
    if (!btn || !viewport) return;
    var expandIcon = btn.querySelector('.rn-raptor-map__fullscreen-icon--expand');
    var collapseIcon = btn.querySelector('.rn-raptor-map__fullscreen-icon--collapse');
    /* In fullscreen the map owns the screen — single-finger pan is
       expected, so suspend cooperative gestures while fullscreen. */
    function setCoopGestures(enabled) {
      if (!map || !map.cooperativeGestures) return;
      if (!isTouchDevice()) return;
      if (enabled && map.cooperativeGestures.enable)  map.cooperativeGestures.enable();
      if (!enabled && map.cooperativeGestures.disable) map.cooperativeGestures.disable();
    }

    btn.addEventListener('click', function() {
      viewport.classList.toggle('is-fullscreen');
      var isFs = viewport.classList.contains('is-fullscreen');
      expandIcon.style.display = isFs ? 'none' : 'block';
      collapseIcon.style.display = isFs ? 'block' : 'none';
      setCoopGestures(!isFs);
      setTimeout(function() { map.resize(); }, 350);
    });
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && viewport.classList.contains('is-fullscreen')) {
        viewport.classList.remove('is-fullscreen');
        expandIcon.style.display = 'block';
        collapseIcon.style.display = 'none';
        setCoopGestures(true);
        setTimeout(function() { map.resize(); }, 350);
      }
    });
  }

  function bindHoverTooltips() {
    hoverPopup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: [0, -36], className: 'rn-hover-tooltip' });
    map.on('mousemove', 'raptor-pins', function(e) {
      if (!e.features || e.features.length === 0) return;
      var p = e.features[0].properties;
      var name = (p.name || p.run_name || p.title || 'Event').trim();
      var organizer = p.organizer ? p.organizer.trim() : '';
      var type = p.event_type ? formatType(p.event_type) : '';
      var date = p.start_date ? formatDate(p.start_date) : '';
      var html = '<strong class="rn-tooltip__name">' + escapeHTML(name) + '</strong>';
      if (organizer) html += '<span class="rn-tooltip__organizer">' + escapeHTML(organizer) + '</span>';
      if (type) html += '<span class="rn-tooltip__type">' + escapeHTML(type) + '</span>';
      if (date) html += '<span class="rn-tooltip__date">' + escapeHTML(date) + '</span>';
      hoverPopup.setLngLat(e.lngLat).setHTML(html).addTo(map);
    });
    map.on('mouseleave', 'raptor-pins', function() { hoverPopup.remove(); });
  }

  function bindPinClick() {
    map.on('click', 'raptor-pins', function(e) {
      if (!e.features || e.features.length === 0) return;
      var f = e.features[0];
      var p = f.properties;
      var coords = f.geometry.coordinates.slice();
      if (hoverPopup) hoverPopup.remove();
      var panel = document.getElementById(config.panelId);
      var content = document.getElementById(config.panelContentId);
      if (!panel || !content) return;
      content.innerHTML = config.isMember ? buildMemberPanel(p) : buildPublicPanel(p);
      panel.classList.add('is-open');
      map.flyTo({ center: coords, zoom: Math.max(map.getZoom(), 8), duration: 800, padding: panelPadding(panel), essential: true });
      setTimeout(function() {
        var scrollY = window.scrollY;
        map.resize();
        window.scrollTo(0, scrollY);
      }, 900);

      /* Dispatch to card list */
      window.dispatchEvent(new CustomEvent('rn:pin-click', {
        detail: { runId: p.run_id || '' }
      }));
    });
    map.on('click', function(e) {
      var pf = map.queryRenderedFeatures(e.point, { layers: ['raptor-pins'] });
      var cf = map.queryRenderedFeatures(e.point, { layers: ['raptor-clusters'] });
      if (pf.length === 0 && cf.length === 0) closePanel();
    });
  }

  function closePanel() {
    var panel = document.getElementById(config.panelId);
    if (panel && panel.classList.contains('is-open')) {
      panel.classList.remove('is-open');
      setTimeout(function() {
        var scrollY = window.scrollY;
        map.resize();
        window.scrollTo(0, scrollY);
      }, 350);
    }
  }

  function bindPanelClose() {
    var closeBtn = document.getElementById(config.panelCloseId);
    if (closeBtn) closeBtn.addEventListener('click', function() { closePanel(); });
  }

  function bindReset() {
    var btn = document.getElementById(config.resetId);
    if (!btn) return;
    btn.addEventListener('click', function() {
      map.flyTo({ center: config.defaultCenter, zoom: config.defaultZoom, pitch: 0, bearing: 0, duration: 1000, essential: true });
    });
  }

  /* bindFilters() removed — filter UI lives in rn-map-filters section.
     This map subscribes to rn:map-filter custom events instead. */

  /* ══════════════════════════════════════════
     REGION MAP — State/Province → Region
     ══════════════════════════════════════════ */
  var REGION_MAP = {
    /* US Northeast */
    'CT':'northeast','DC':'northeast','DE':'northeast','MA':'northeast','MD':'northeast',
    'ME':'northeast','NH':'northeast','NJ':'northeast','NY':'northeast','PA':'northeast',
    'RI':'northeast','VT':'northeast',
    /* US Southeast */
    'AL':'southeast','AR':'southeast','FL':'southeast','GA':'southeast','KY':'southeast',
    'LA':'southeast','MS':'southeast','NC':'southeast','SC':'southeast','TN':'southeast',
    'VA':'southeast','WV':'southeast',
    /* US Midwest */
    'IA':'midwest','IL':'midwest','IN':'midwest','KS':'midwest','MI':'midwest',
    'MN':'midwest','MO':'midwest','ND':'midwest','NE':'midwest','OH':'midwest',
    'SD':'midwest','WI':'midwest',
    /* US Southwest */
    'AZ':'southwest','NM':'southwest','OK':'southwest','TX':'southwest',
    /* US West */
    'AK':'west','CA':'west','CO':'west','HI':'west','ID':'west','MT':'west',
    'NV':'west','OR':'west','UT':'west','WA':'west','WY':'west',
    /* Mexico */
    'CH':'mexico','COA':'mexico','NLE':'mexico','AGS':'mexico','BC':'mexico_or_canada',
    'BCS':'mexico','CAM':'mexico','CHP':'mexico','DGO':'mexico','GRO':'mexico',
    'GTO':'mexico','HGO':'mexico','JAL':'mexico','MEX':'mexico','MIC':'mexico',
    'MOR':'mexico','NAY':'mexico','OAX':'mexico','PUE':'mexico','QRO':'mexico',
    'ROO':'mexico','SIN':'mexico','SLP':'mexico','SON':'mexico','TAB':'mexico',
    'TAM':'mexico','TLX':'mexico','VER':'mexico','YUC':'mexico','ZAC':'mexico',
    /* Canada */
    'ON':'canada','NS':'canada','AB':'canada','MB':'canada','NB':'canada',
    'NL':'canada','NT':'canada','NU':'canada','PE':'canada','QC':'canada',
    'SK':'canada','YT':'canada'
  };

  /* BC is ambiguous — could be British Columbia or Baja California.
     Resolve by checking country field. */
  function getRegion(properties) {
    var state = (properties.state || '').trim().toUpperCase();
    var country = (properties.country || '').trim().toLowerCase();

    /* Handle BC ambiguity */
    if (state === 'BC') {
      if (country === 'mexico' || country === 'mx') return 'mexico';
      return 'canada';
    }

    return REGION_MAP[state] || '';
  }

  /* Filter state held at module scope; updated by rn:map-filter listener.
     Source of truth lives in rn-map-filters section. */
  var mapFilterState = {
    eventsOn:    true,
    types:       [],
    region:      'all',
    showClubs:   false,
    hidePast:    true,
    month:       null,
    year:        null,
    vendorsOn:   false,
    vendorTypes: []
  };

  function applyFilters() {
    if (!geojsonData || !map) return;

    var filtered = {
      type: 'FeatureCollection',
      features: geojsonData.features.filter(function(f) {
        var p = f.properties || {};
        var type = normalizeType(p.event_type || '');

        /* Clubs are surfaced via their own toggle. OFF = hide; ON = include
           alongside whatever else matches the type filter. */
        if (type === 'club') {
          if (!mapFilterState.showClubs) return false;
          if (mapFilterState.region !== 'all') {
            if (getRegion(p) !== mapFilterState.region) return false;
          }
          return true;
        }

        /* Events master toggle — OFF hides all non-club features. */
        if (!mapFilterState.eventsOn) return false;

        /* Type filter — empty = all event types */
        if (mapFilterState.types.length > 0) {
          if (mapFilterState.types.indexOf(type) === -1) return false;
        }

        /* Region filter */
        if (mapFilterState.region !== 'all') {
          if (getRegion(p) !== mapFilterState.region) return false;
        }

        /* Date filters */
        var startDate = p.start_date ? new Date(p.start_date) : null;
        if (startDate && !isNaN(startDate.getTime())) {
          if (mapFilterState.hidePast) {
            var todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
            if (startDate < todayStart) return false;
          }
          if (mapFilterState.month !== null && mapFilterState.year !== null) {
            if (startDate.getMonth() + 1 !== mapFilterState.month) return false;
            if (startDate.getFullYear() !== mapFilterState.year)   return false;
          }
        }

        return true;
      })
    };

    map.getSource('raptor-runs').setData(filtered);
    lastFilteredCount = filtered.features.length;
    closePanel();
    updateCount();

    /* Broadcast count back to filter bar */
    window.dispatchEvent(new CustomEvent('rn:map-count', {
      detail: { count: lastFilteredCount }
    }));
  }

  /* ══════════════════════════════════════════
     CARD LIST SYNC — Listen for card events
     ══════════════════════════════════════════ */
  function listenForCardEvents() {
    /* Card click → fly map to pin + open panel */
    window.addEventListener('rn:card-click', function(e) {
      if (!map || !e.detail) return;
      var lng = e.detail.lng;
      var lat = e.detail.lat;
      var p = e.detail.properties;
      if (isNaN(lng) || isNaN(lat)) return;

      /* Open side panel if we have properties */
      if (p) {
        var panel = document.getElementById(config.panelId);
        var content = document.getElementById(config.panelContentId);
        if (panel && content) {
          if (e.detail.isVendor) {
            content.innerHTML = buildVendorPanel(p);
          } else {
            content.innerHTML = config.isMember ? buildMemberPanel(p) : buildPublicPanel(p);
          }
          panel.classList.add('is-open');
          map.flyTo({
            center: [lng, lat],
            zoom: Math.max(map.getZoom(), 8),
            duration: 800,
            padding: panelPadding(panel),
            essential: true
          });
          setTimeout(function() {
            var scrollY = window.scrollY;
            map.resize();
            window.scrollTo(0, scrollY);
          }, 900);
          return;
        }
      }

      map.flyTo({ center: [lng, lat], zoom: Math.max(map.getZoom(), 10), duration: 800, essential: true });
    });

    /* Filter state from rn-map-filters → re-filter map */
    window.addEventListener('rn:map-filter', function(e) {
      if (!e.detail) return;
      var d = e.detail;

      if (typeof d.eventsOn === 'boolean')   mapFilterState.eventsOn = d.eventsOn;
      if (Array.isArray(d.types))            mapFilterState.types     = d.types.slice();
      if (typeof d.region === 'string')      mapFilterState.region    = d.region;
      if (typeof d.showClubs === 'boolean')  mapFilterState.showClubs = d.showClubs;
      if (typeof d.hidePast === 'boolean')   mapFilterState.hidePast  = d.hidePast;
      if (d.month === null || typeof d.month === 'number') mapFilterState.month = d.month;
      if (d.year  === null || typeof d.year  === 'number') mapFilterState.year  = d.year;
      if (typeof d.vendorsOn === 'boolean')  mapFilterState.vendorsOn   = d.vendorsOn;
      if (Array.isArray(d.vendorTypes))      mapFilterState.vendorTypes = d.vendorTypes.slice();

      applyFilters();
      applyVendorFilters();
    });
  }

  var lastFilteredCount = 0;

  function updateCount() {
    var countEl = document.getElementById(config.counterId);
    if (countEl) countEl.textContent = lastFilteredCount;
  }

  /* ══════════════════════════════════════════
     PANEL BUILDERS
     ══════════════════════════════════════════ */
  function buildPanelHeader(p) {
    var name = (p.name || p.run_name || p.title || 'Untitled Event').trim();
    var organizer = p.organizer ? p.organizer.trim() : '';
    var eventType = p.event_type ? p.event_type.trim() : '';
    var pinColor = getPinColor(eventType);
    var html = '';

    html += '<div class="rn-panel__header">';
    html += '<div class="rn-panel__logo-wrap">';
    if (p.organizer_logo_url) {
      html += '<img class="rn-panel__logo-img" src="' + escapeHTML(p.organizer_logo_url) + '" alt="' + escapeHTML(organizer || name) + '">';
    } else {
      html += '<div class="rn-panel__logo-monogram" style="background:' + pinColor + '20;color:' + pinColor + ';">' + escapeHTML(name.charAt(0).toUpperCase()) + '</div>';
    }
    html += '</div></div>';

    html += '<div class="rn-panel__body">';
    if (eventType) html += '<span class="rn-panel__badge ' + typeToBadgeClass(eventType) + '">' + escapeHTML(formatType(eventType)) + '</span>';
    if (organizer) html += '<div class="rn-panel__organizer">' + escapeHTML(organizer) + '</div>';
    html += '<h3 class="rn-panel__name">' + escapeHTML(name) + '</h3>';
    if (p.description) html += '<p class="rn-panel__description">' + escapeHTML(p.description) + '</p>';
    html += '</div>';
    return html;
  }

  function buildPublicMeta(p) {
    var html = '<div class="rn-panel__meta rn-panel__meta--divider">';
    var locationParts = [p.city, p.state, p.country].filter(function(v) { return v && String(v).trim(); });
    if (locationParts.length) html += metaRow(iconPin(), escapeHTML(locationParts.join(', ')));
    if (p.start_date) {
      var dateStr = formatDate(p.start_date);
      if (p.end_date && p.end_date !== p.start_date) dateStr += ' — ' + formatDate(p.end_date);
      html += metaRow(iconCal(), escapeHTML(dateStr));
    }
    if (p.website) html += metaRow(iconGlobe(), '<a class="rn-panel__link" href="' + escapeHTML(p.website) + '" target="_blank" rel="noopener">Website</a>');
    html += '</div>';
    return html;
  }

  function buildPublicPanel(p) {
    var html = buildPanelHeader(p);
    html += buildPublicMeta(p);
    html += '<div class="rn-panel__meta rn-panel__meta--divider rn-panel__gated-wrap">';
    html += '<div class="rn-panel__gated-blur">';
    html += metaRow(iconDiscount(), 'Member discount available');
    html += metaRow(iconPrice(), '$---');
    html += metaRow(iconLink(), 'Registration link');
    html += '</div>';
    html += '<div class="rn-panel__gated-lock">' + iconLock() + '<span class="rn-panel__gated-label">Premium Exclusive</span><a href="/pages/membership" class="rn-panel__gated-btn">Join the Nation</a></div>';
    html += '</div>';
    html += buildTags(p);
    return html;
  }

  function buildMemberPanel(p) {
    var html = buildPanelHeader(p);
    html += buildPublicMeta(p);
    html += '<div class="rn-panel__meta rn-panel__meta--divider">';
    if (p.premium_discount_available === 'yes' || p.premium_discount_available === true) {
      var discountText = p.discount_details ? escapeHTML(p.discount_details) : 'Member discount available';
      if (p.discount_code) discountText += ' <span class="rn-panel__code">' + escapeHTML(p.discount_code) + '</span>';
      html += metaRow(iconDiscount(), discountText);
    }
    if (p.price_usd && Number(p.price_usd) > 0) html += metaRow(iconPrice(), '$' + escapeHTML(String(p.price_usd)));
    else if (String(p.price_usd) === '0' || p.price_usd === 0) html += metaRow(iconPrice(), 'Free');
    if (p.website) html += metaRow(iconLink(), '<a class="rn-panel__link" href="' + escapeHTML(p.website) + '" target="_blank" rel="noopener">Registration / Info</a>');
    html += '</div>';
    if (p.photo_1 || p.photo_2 || p.photo_3) {
      html += '<div class="rn-panel__photos">';
      [p.photo_1, p.photo_2, p.photo_3].forEach(function(src) {
        if (src) html += '<div class="rn-panel__photo-item"><img src="' + escapeHTML(src) + '" alt=""></div>';
      });
      html += '</div>';
    }
    html += buildTags(p);
    return html;
  }

  function buildTags(p) {
    var tags = [];
    if (p.difficulty_level && String(p.difficulty_level).trim()) tags.push(formatType(p.difficulty_level));
    if (p.beginner_friendly === 'yes' || p.beginner_friendly === true) tags.push('Beginner Friendly');
    if (p.vehicle_requirements && String(p.vehicle_requirements).trim() && p.vehicle_requirements !== 'n/a') tags.push(formatType(p.vehicle_requirements));
    if (p.featured === 'yes' || p.featured === true) tags.push('Featured');
    if (!tags.length) return '';
    return '<div class="rn-panel__tags">' + tags.map(function(t) { return '<span class="rn-panel__tag">' + escapeHTML(t) + '</span>'; }).join('') + '</div>';
  }

  /* ══════════════════════════════════════════
     META ROWS + ICONS
     ══════════════════════════════════════════ */
  function metaRow(icon, content) {
    return '<div class="rn-panel__meta-row"><span class="rn-panel__meta-icon">' + icon + '</span><span class="rn-panel__meta-value">' + content + '</span></div>';
  }

  function svgIcon(path, extra) {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="16" height="16"' + (extra || '') + '>' + path + '</svg>';
  }

  function iconPin()      { return svgIcon('<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>'); }
  function iconCal()      { return svgIcon('<rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>'); }
  function iconGlobe()    { return svgIcon('<circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>'); }
  function iconDiscount() { return svgIcon('<path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/>'); }
  function iconPrice()    { return svgIcon('<line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>'); }
  function iconLink()     { return svgIcon('<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>'); }
  function iconLock()     { return svgIcon('<rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>', ' width="28" height="28"'); }

  /* ══════════════════════════════════════════
     UTILITIES
     ══════════════════════════════════════════ */
  function escapeHTML(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function formatType(str) {
    if (!str) return '';
    return str.trim().split(' ').map(function(w) { return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(); }).join(' ');
  }

  function typeToBadgeClass(t) {
    var m = {
      /* Runs — green */
      'run & event - multi day': 'rn-panel__badge--run',
      'run - multi day':         'rn-panel__badge--run',
      'run - one day':           'rn-panel__badge--run',
      'run & event - one day':   'rn-panel__badge--run',
      /* Events */
      'ford event':              'rn-panel__badge--ford',
      'race':                    'rn-panel__badge--event',
      'expo':                    'rn-panel__badge--event',
      'event - multi day':       'rn-panel__badge--event',
      'car show':                'rn-panel__badge--event',
      /* Social */
      'caravan':                 'rn-panel__badge--social',
      'charity':                 'rn-panel__badge--social',
      'meetup':                  'rn-panel__badge--social',
      /* Club — separate toggle */
      'club':                    'rn-panel__badge--club'
    };
    return m[normalizeType(t)] || 'rn-panel__badge--default';
  }

  function formatDate(dateStr) {
    if (!dateStr) return '';
    try {
      var d = new Date(dateStr);
      if (isNaN(d.getTime())) return String(dateStr).split('T')[0];
      var months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return months[d.getUTCMonth()] + ' ' + d.getUTCDate() + ', ' + d.getUTCFullYear();
    } catch(e) { return String(dateStr).split('T')[0]; }
  }

  function publicResize() { if (map) map.resize(); }

  return { init: init, resize: publicResize };

})();